import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type DragEvent } from "react";
import type { DocAttachment, WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { imageMarkdown } from "@/modules/docs/markdown";

/** The same cap `apps/api/src/routes/docs.ts` enforces server-side — checked
 * here too so a 25 MB+ file never makes the round trip just to be refused. */
const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/** The markdown a "Copy link" writes into the draft (§7.8) — the app's own
 * route, never R2: the reading view's link allowlist already permits a
 * relative URL, and this one resolves to a client route that hands off to
 * the API's authenticated download (`routes/router.tsx`'s own comment on
 * `/docs/attachments/$id` says why the indirection exists at all). */
function attachmentLinkMarkdown(attachment: DocAttachment): string {
  return `[${attachment.filename}](/docs/attachments/${attachment.id})`;
}

/** What the page's attachments are and what can be done with them. */
export type AttachmentsState = ReturnType<typeof useAttachments>;

/**
 * A page's files (FR-DOC-12) and the actions on them, as a hook (#641), so
 * the page can offer "Attach a file" in its "⋯" menu and take a dropped file
 * anywhere on itself, while the list only appears once there is something
 * in it.
 */
export function useAttachments(pageId: string) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const attachmentsQuery = useQuery({
    queryKey: ["docs", "attachments", pageId],
    queryFn: () => data.docs.listAttachments(pageId),
  });
  const attachments = attachmentsQuery.data ?? [];

  function invalidate() {
    void queryClient.invalidateQueries({
      queryKey: ["docs", "attachments", pageId],
    });
  }

  const uploadMutation = useMutation({
    mutationFn: async (file: File) =>
      data.docs.uploadAttachment(pageId, {
        filename: file.name,
        contentType: file.type || "application/octet-stream",
        data: new Uint8Array(await file.arrayBuffer()),
      }),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        invalidate();
      } else {
        setRejection(result.rejection);
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => data.docs.deleteAttachment(id),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        invalidate();
      } else {
        setRejection(result.rejection);
      }
    },
  });

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        setRejection({
          kind: "invalid",
          message: `"${file.name}" is over the 25 MB limit.`,
          fields: { file: "Too large" },
        });
        continue;
      }
      uploadMutation.mutate(file);
    }
  }

  /**
   * An image pasted into the text (#628): stored as one of the page's
   * attachments like any other file, and answered with the markdown that
   * shows it — or null, with the reason in `rejection`, when it could not be.
   * A pasted screenshot arrives called "image.png", so the name gets a time
   * to tell two of them apart in the list.
   */
  async function uploadInline(file: File): Promise<string | null> {
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setRejection({
        kind: "invalid",
        message: `"${file.name}" is over the 25 MB limit.`,
        fields: { file: "Too large" },
      });
      return null;
    }
    const extension = /\.[A-Za-z0-9]+$/.exec(file.name)?.[0] ?? ".png";
    const stamp = new Date()
      .toISOString()
      .replace(/\.\d+Z$/, "")
      .replace(/[-:]/g, "")
      .replace("T", "-");
    const generic = /^image\.[A-Za-z0-9]+$/i.test(file.name);
    const filename = generic ? `image-${stamp}${extension}` : file.name;
    try {
      const result = await data.docs.uploadAttachment(pageId, {
        filename,
        contentType: file.type || "application/octet-stream",
        data: new Uint8Array(await file.arrayBuffer()),
      });
      if (!result.ok) {
        setRejection(result.rejection);
        return null;
      }
      setRejection(null);
      invalidate();
      return imageMarkdown(
        filename.replace(/\.[A-Za-z0-9]+$/, ""),
        result.data.id,
      );
    } catch {
      setRejection({
        kind: "invalid",
        message: "The image could not be uploaded. Try again.",
        fields: {},
      });
      return null;
    }
  }

  function requestDelete(attachment: DocAttachment) {
    // The same native `window.confirm` pattern `Browse.tsx` already uses for
    // deleting a page — the only destructive-confirm pattern this codebase
    // has needed so far.
    if (
      window.confirm(`Delete "${attachment.filename}"? This cannot be undone.`)
    ) {
      deleteMutation.mutate(attachment.id);
    }
  }

  function copyLink(attachment: DocAttachment) {
    void navigator.clipboard
      .writeText(attachmentLinkMarkdown(attachment))
      .then(() => {
        setCopiedId(attachment.id);
        window.setTimeout(() => {
          setCopiedId((current) =>
            current === attachment.id ? null : current,
          );
        }, 1500);
      });
  }

  /** Drag-and-drop onto whatever element spreads these (the whole page). */
  function dropProps(canWrite: boolean) {
    if (!canWrite) return {};
    return {
      onDragOver: (event: DragEvent) => {
        if (!event.dataTransfer.types.includes("Files")) return;
        event.preventDefault();
        setDragOver(true);
      },
      onDragLeave: () => {
        setDragOver(false);
      },
      onDrop: (event: DragEvent) => {
        if (event.dataTransfer.files.length === 0) return;
        event.preventDefault();
        setDragOver(false);
        handleFiles(event.dataTransfer.files);
      },
    };
  }

  /** The hidden picker "Attach a file" opens. */
  const picker = (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      className="hidden"
      onChange={(event) => {
        handleFiles(event.target.files);
        event.target.value = "";
      }}
    />
  );

  return {
    attachments,
    pending: attachmentsQuery.isPending,
    uploading: uploadMutation.isPending,
    rejection,
    dragOver,
    copiedId,
    picker,
    openPicker: () => fileInputRef.current?.click(),
    dropProps,
    uploadInline,
    requestDelete,
    copyLink,
  };
}
